
import { defineComponent, PropType } from 'vue';
import {
    ArrowDown, ArrowUp, X, Scale, Download, Upload, Clock, Check, Ban, RotateCcw,
    User, Bitcoin, Snowflake, RefreshCw, CreditCard, Coins, Settings, Hand,
    AlertTriangle, TrendingUp, FileText, Filter, RefreshCw as RefreshIcon,
    ChevronDown, ChevronLeft, ChevronRight, Check as CheckIcon, X as XIcon, FileDown, Receipt, DollarSign,
    TrendingUp as TrendingUpIcon, Activity, Eye
} from 'lucide-vue-next';

interface StatementLog {
    id: string;
    type: string;
    action: string;
    userId: string;
    adminId?: string | null;
    targetUserId?: string | null;
    amount: string;
    currency: string | null;
    previousBalance: string | null;
    newBalance: string | null;
    description: string;
    reason?: string | null;
    metadata?: Record<string, unknown> | null;
    ipAddress?: string | null;
    userAgent?: string | null;
    device?: string | null;
    location?: string | null;
    referenceId: string;
    relatedTable?: string | null;
    status: string;
    errorMessage?: string | null;
    isRead: boolean;
    createdAt: string;
    user?: {
        id: string;
        name: string;
        email: string;
        mail?: string;
    };
    admin?: {
        id: string;
        name: string;
        email: string;
    } | null;
    targetUser?: {
        id: string;
        name: string;
        email: string;
        mail?: string | null;
    } | null;
}

interface FinancialBreakdown {
    income: number;
    expense: number;
    netChange: number;
    formattedIncome: string;
    formattedExpense: string;
    formattedNetChange: string;
}

interface Summary {
    totalRiali: number;
    totalUSDT: number;
    totalLogs: number;
    financialBreakdown?: {
        riali: FinancialBreakdown;
        usdt: FinancialBreakdown;
    };
}


interface Pagination {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasMore: boolean;
}

interface StatementUser {
    id: string;
    name: string;
    email: string;
}

export default defineComponent({
    name: "UserStatement",

    components: {
        ArrowDown, ArrowUp, X, Scale, Download, Upload, Clock, Check, Ban, RotateCcw,
        User, Bitcoin, Snowflake, RefreshCw, CreditCard, Coins, Settings, Hand,
        AlertTriangle, TrendingUp, FileText, Filter, RefreshIcon,
        ChevronDown, ChevronLeft, ChevronRight, CheckIcon, XIcon, FileDown, Receipt, DollarSign,
        TrendingUpIcon, Activity, Eye
    },

    props: {
        logs: {
            type: Array as PropType<StatementLog[]>,
            required: true,
            default: () => []
        },
        summary: {
            type: Object as PropType<Summary>,
            required: true,
            default: () => ({
                totalRiali: 0,
                totalUSDT: 0,
                totalLogs: 0
            })
        },
        pagination: {
            type: Object as PropType<Pagination>,
            required: true,
            default: () => ({
                page: 1,
                pageSize: 50,
                totalItems: 0,
                totalPages: 0,
                hasMore: false
            })
        },
        user: {
            type: Object as PropType<StatementUser | null>,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            showFilters: false,
            showTooltip: null as string | null,
            showMetadataDialog: false,
            selectedLog: null as StatementLog | null,
            localFilters: {
                category: undefined as "charge" | "payment" | "withdraw" | "transfer" | "commission" | "settlement" | "crypto" | "all" | undefined,
                currency: undefined as string | undefined,
                minAmount: undefined as number | undefined,
                maxAmount: undefined as number | undefined,
                status: undefined as "SUCCESS" | "FAILED" | "PENDING" | "CANCELLED" | undefined,
                from: undefined as string | undefined,
                to: undefined as string | undefined,
            },
            categories: [
                { value: undefined, label: 'همه تراکنش‌ها', icon: 'FileText' },
                { value: 'charge' as const, label: 'شارژ کیف پول', icon: 'ArrowDown' },
                { value: 'payment' as const, label: 'پرداخت', icon: 'CreditCard' },
                { value: 'withdraw' as const, label: 'برداشت', icon: 'ArrowUp' },
                { value: 'transfer' as const, label: 'انتقال وجه', icon: 'RefreshCw' },
                { value: 'commission' as const, label: 'کارمزد', icon: 'Coins' },
                { value: 'settlement' as const, label: 'تسویه', icon: 'Scale' },
                { value: 'crypto' as const, label: 'کریپتو', icon: 'Bitcoin' },
            ]
        };
    },

    computed: {
        activeFiltersCount(): number {
            let count = 0;
            if (this.localFilters.category) count++;
            if (this.localFilters.currency) count++;
            if (this.localFilters.minAmount !== undefined) count++;
            if (this.localFilters.maxAmount !== undefined) count++;
            if (this.localFilters.status) count++;
            if (this.localFilters.from) count++;
            if (this.localFilters.to) count++;
            return count;
        }
    },

    methods: {
        selectCategory(category: "charge" | "payment" | "withdraw" | "transfer" | "commission" | "settlement" | "crypto" | "all" | undefined): void {
            this.localFilters.category = category;
        },

        refreshData(): void {
            this.$emit('refresh');
        },

        exportData(): void {
            this.$emit('export');
        },

        formatNetChange(): string {
            if (this.summary.financialBreakdown?.riali) {
                const netChange = this.summary.financialBreakdown.riali.netChange;
                const sign = netChange >= 0 ? '+' : '';
                return `${sign}${this.formatCurrency(Math.abs(netChange))}`;
            }
            return '0';
        },

        getNetChangeClass(): string {
            if (this.summary.financialBreakdown?.riali) {
                const netChange = this.summary.financialBreakdown.riali.netChange;
                return netChange >= 0 ? 'amount-credit' : 'amount-debit';
            }
            return '';
        },

        getActionSubtitle(action: string): string {
            const subtitles: Record<string, string> = {
                'CHARGE_SUCCESS': 'شارژ موفق',
                'WALLET_CHARGE': 'شارژ کیف پول',
                'WALLET_DECHARGE': 'کسر از کیف پول',
                'WALLET_DEDUCT': 'برداشت از کیف پول',
                'BALANCE_DEDUCTED': 'کسر موجودی',
                'TRANSFER_RECEIVED': 'دریافت انتقال',
                'TRANSFER_SENT': 'ارسال انتقال',
                'WITHDRAWAL_SETTLEMENT_START': 'شروع تسویه',
                'WITHDRAWAL_COMPLETED': 'تسویه کامل',
                'WITHDRAWAL_FAILED': 'تسویه ناموفق',
                'WITHDRAWAL_CANCELLATION': 'لغو تسویه',
                'WITHDRAWAL_REFUND': 'بازگشت تسویه',
                'ADMIN_CHARGE': 'شارژ ادمین',
                'ADMIN_DEDUCT': 'کسر ادمین',
                'CRYPTO_WITHDRAWAL': 'برداشت کریپتو',
                'CRYPTO_DEPOSIT': 'واریز کریپتو',
                'CRYPTO_WITHDRAWAL_FROZEN': 'فریز کریپتو',
                'USDT_TRANSFER': 'انتقال تتر',
                'USDT_CHARGE': 'شارژ تتر',
                'PAYMENT_SUCCESS': 'پرداخت موفق',
                'PAYMENT_FAILED': 'پرداخت ناموفق',
                'COMMISSION_DEDUCTION': 'کسر کارمزد',
                'FEE_DEDUCTED': 'کسر هزینه',
                'SETTLEMENT_SUCCESS': 'تسویه موفق',
                'SETTLEMENT_COMPLETED': 'تسویه کامل',
                'SETTLEMENT_PROCESSED': 'تسویه پردازش شده'
            };
            return subtitles[action] || 'عملیات مالی';
        },

        getCurrencyClass(currency: string | null): string {
            if (!currency) return 'currency-default';
            const currencyUpper = currency.toUpperCase();
            if (['RIALI', 'IR', 'IRR', 'IRT', 'RIAL'].includes(currencyUpper)) {
                return 'currency-rial';
            } else if (['USDT', 'USD'].includes(currencyUpper)) {
                return 'currency-usdt';
            }
            return 'currency-default';
        },

        getMethodClass(action: string): string {
            if (action.includes('GATEWAY') || action.includes('CHARGE')) {
                return 'method-gateway';
            } else if (action.includes('ADMIN')) {
                return 'method-admin';
            } else if (action.includes('SYSTEM') || action.includes('BALANCE')) {
                return 'method-system';
            } else if (action.includes('TRANSFER')) {
                return 'method-transfer';
            } else if (action.includes('WITHDRAWAL')) {
                return 'method-withdrawal';
            } else if (action.includes('CRYPTO')) {
                return 'method-crypto';
            }
            return 'method-default';
        },

        applyFilters(): void {
            // Only emit filters that have values (not undefined, not empty string)
            const activeFilters: Record<string, string> = {};

            if (this.localFilters.category) {
                activeFilters.category = this.localFilters.category;
            }
            if (this.localFilters.currency) {
                activeFilters.currency = this.localFilters.currency;
            }
            if (this.localFilters.minAmount) {
                activeFilters.minAmount = this.localFilters.minAmount.toString();
            }
            if (this.localFilters.maxAmount) {
                activeFilters.maxAmount = this.localFilters.maxAmount.toString();
            }
            if (this.localFilters.status) {
                activeFilters.status = this.localFilters.status;
            }
            if (this.localFilters.from) {
                activeFilters.from = this.localFilters.from;
            }
            if (this.localFilters.to) {
                activeFilters.to = this.localFilters.to;
            }

            this.$emit('filter-change', activeFilters);
        },

        clearFilters(): void {
            this.localFilters = {
                category: undefined,
                currency: undefined,
                minAmount: undefined,
                maxAmount: undefined,
                status: undefined,
                from: undefined,
                to: undefined,
            };
            // Emit empty object to clear all filters
            this.$emit('filter-change', {});
        },

        formatNumber(value: number | string): string {
            if (!value) return '0';
            const num = typeof value === 'string' ? parseFloat(value) : value;
            return new Intl.NumberFormat('fa-IR').format(num);
        },

        formatCurrency(value: number | string): string {
            if (!value) return '0';
            const num = typeof value === 'string' ? parseFloat(value) : value;
            return new Intl.NumberFormat('fa-IR').format(num);
        },

        formatDate(dateString: string): string {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('fa-IR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        },

        formatDateShort(dateString: string): string {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('fa-IR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).format(date);
        },

        formatTime(dateString: string): string {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('fa-IR', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            }).format(date);
        },

        getCurrencyLabel(currency: string | null): string {
            if (!currency) return 'نامشخص';
            const labels: Record<string, string> = {
                'IRT': 'تومان',
                'IR': 'ریال',
                'RIALI': 'ریال',
                'IRR': 'ریال',
                'USDT': 'تتر',
                'USD': 'دلار'
            };
            return labels[currency] || currency;
        },

        getMethodLabel(action: string, type?: string): string {
            const methods: Record<string, string> = {
                'WALLET_CHARGE': 'Gateway',
                'WALLET_DECHARGE': 'System',
                'WALLET_DEDUCT': 'Manual',
                'BALANCE_DEDUCTED': 'System Balance',
                'TRANSFER_RECEIVED': 'Internal Transfer',
                'TRANSFER_SENT': 'Internal Transfer',
                'WITHDRAWAL_SETTLEMENT_START': 'Bank Payout',
                'WITHDRAWAL_COMPLETED': 'Bank Payout',
                'WITHDRAWAL_FAILED': 'Bank Payout',
                'ADMIN_CHARGE': 'Admin Panel',
                'ADMIN_DEDUCT': 'Admin Panel'
            };
            return methods[action] || type || 'Unknown';
        },

        getActionTitle(action: string): string {
            const titles: Record<string, string> = {
                'CHARGE_SUCCESS': 'شارژ کیف پول',
                'WALLET_CHARGE': 'شارژ کیف پول',
                'WALLET_CHARGE_SUCCESS': 'شارژ موفق',
                'WALLET_CHARGE_FAILED': 'شارژ ناموفق',
                'WALLET_DECHARGE': 'کسر از کیف پول',
                'WALLET_DEDUCT': 'برداشت از کیف پول',
                'BALANCE_DEDUCTED': 'کسر موجودی',
                'TRANSFER_RECEIVED': 'دریافت انتقال',
                'TRANSFER_SENT': 'ارسال انتقال',
                'WITHDRAWAL_SETTLEMENT_START': 'شروع تسویه',
                'WITHDRAWAL_COMPLETED': 'تسویه کامل',
                'WITHDRAWAL_FAILED': 'تسویه ناموفق',
                'WITHDRAWAL_CANCELLATION': 'لغو تسویه',
                'WITHDRAWAL_REFUND': 'بازگشت تسویه',
                'ADMIN_CHARGE': 'شارژ ادمین',
                'ADMIN_DEDUCT': 'کسر ادمین',
                'CRYPTO_WITHDRAWAL': 'برداشت کریپتو',
                'CRYPTO_DEPOSIT': 'واریز کریپتو',
                'CRYPTO_WITHDRAWAL_FROZEN': 'فریز کریپتو',
                'USDT_TRANSFER': 'انتقال تتر',
                'USDT_CHARGE': 'شارژ تتر',
                'PAYMENT_SUCCESS': 'پرداخت موفق',
                'PAYMENT_FAILED': 'پرداخت ناموفق',
                'PAYMENT_DEDUCTION': 'کسر پرداخت',
                'COMMISSION_DEDUCTION': 'کسر کارمزد',
                'FEE_DEDUCTED': 'کسر هزینه',
                'SETTLEMENT_SUCCESS': 'تسویه موفق',
                'SETTLEMENT_COMPLETED': 'تسویه کامل',
                'SETTLEMENT_PROCESSED': 'تسویه پردازش شده',
                'MANUAL_WALLET_CHARGE': 'شارژ دستی',
                'DEPOSIT_SUCCESS': 'واریز موفق',
                'AMOUNT_TRANSFERRED': 'انتقال مبلغ',
                'PAYMENT_COMPLETED': 'پرداخت تکمیل شده',
                'WITHDRAWAL_SUCCESS': 'برداشت موفق',
                'WITHDRAWAL_DEDUCTION': 'کسر برداشت',
                'WITHDRAWAL': 'برداشت',
                'WITHDRAW': 'برداشت',
                'WITHDRAWAL_INSUFFICIENT_RESERVED': 'موجودی ناکافی',
                'TRANSFER_SUCCESS': 'انتقال موفق',
                'TRANSFER_DEDUCTION': 'کسر انتقال',
                'TRANSFER': 'انتقال',
                'TRANSFER_SEND': 'ارسال انتقال',
                'TRANSFER_RECEIVE': 'دریافت انتقال',
                'QR_TRANSFER_OUT': 'انتقال QR خروجی',
                'QR_TRANSFER_IN': 'انتقال QR ورودی',
                'COMMISSION_SUCCESS': 'کارمزد موفق',
                'CRYPTO_SUCCESS': 'کریپتو موفق',
                'CRYPTO_DEDUCTION': 'کسر کریپتو',
                'MARKET_MAKER_TRADE': 'معامله بازارساز',
                'ORDER_FILLED': 'سفارش تکمیل شده',
                'ORDER_CREATED': 'سفارش ایجاد شده',
                'ACCOUNT_STATEMENT_ENTRY': 'ورودی صورتحساب'
            };
            return titles[action] || action;
        },

        getActionType(action: string): string {
            const types: Record<string, string> = {
                'WALLET_CHARGE': 'شارژ کیف پول',
                'WALLET_DECHARGE': 'کسر از کیف پول',
                'WALLET_DEDUCT': 'برداشت از کیف پول',
                'BALANCE_DEDUCTED': 'کسر موجودی',
                'TRANSFER_RECEIVED': 'انتقال وجه',
                'TRANSFER_SENT': 'انتقال وجه',
                'WITHDRAWAL_SETTLEMENT_START': 'تسویه بانکی',
                'WITHDRAWAL_COMPLETED': 'تسویه بانکی',
                'WITHDRAWAL_FAILED': 'تسویه بانکی',
                'ADMIN_CHARGE': 'شارژ توسط ادمین',
                'ADMIN_DEDUCT': 'کسر توسط ادمین'
            };
            return types[action] || 'عملیات';
        },

        getActionIcon(action: string): string {
            const icons: Record<string, string> = {
                'CHARGE_SUCCESS': 'ArrowDown',
                'WALLET_CHARGE': 'ArrowDown',
                'WALLET_CHARGE_SUCCESS': 'ArrowDown',
                'WALLET_CHARGE_FAILED': 'X',
                'WALLET_DECHARGE': 'ArrowUp',
                'WALLET_DEDUCT': 'ArrowUp',
                'BALANCE_DEDUCTED': 'Scale',
                'TRANSFER_RECEIVED': 'Download',
                'TRANSFER_SENT': 'Upload',
                'WITHDRAWAL_SETTLEMENT_START': 'Clock',
                'WITHDRAWAL_COMPLETED': 'Check',
                'WITHDRAWAL_FAILED': 'X',
                'WITHDRAWAL_CANCELLATION': 'Ban',
                'WITHDRAWAL_REFUND': 'RotateCcw',
                'ADMIN_CHARGE': 'User',
                'ADMIN_DEDUCT': 'User',
                'CRYPTO_WITHDRAWAL': 'Bitcoin',
                'CRYPTO_DEPOSIT': 'Bitcoin',
                'CRYPTO_WITHDRAWAL_FROZEN': 'Snowflake',
                'USDT_TRANSFER': 'RefreshCw',
                'USDT_CHARGE': 'RefreshCw',
                'PAYMENT_SUCCESS': 'CreditCard',
                'PAYMENT_FAILED': 'X',
                'PAYMENT_DEDUCTION': 'CreditCard',
                'COMMISSION_DEDUCTION': 'Coins',
                'FEE_DEDUCTED': 'Coins',
                'SETTLEMENT_SUCCESS': 'Scale',
                'SETTLEMENT_COMPLETED': 'Check',
                'SETTLEMENT_PROCESSED': 'Settings',
                'MANUAL_WALLET_CHARGE': 'Hand',
                'DEPOSIT_SUCCESS': 'Download',
                'AMOUNT_TRANSFERRED': 'RefreshCw',
                'PAYMENT_COMPLETED': 'Check',
                'WITHDRAWAL_SUCCESS': 'Check',
                'WITHDRAWAL_DEDUCTION': 'ArrowUp',
                'WITHDRAWAL': 'ArrowUp',
                'WITHDRAW': 'ArrowUp',
                'WITHDRAWAL_INSUFFICIENT_RESERVED': 'AlertTriangle',
                'TRANSFER_SUCCESS': 'Check',
                'TRANSFER_DEDUCTION': 'RefreshCw',
                'TRANSFER': 'RefreshCw',
                'TRANSFER_SEND': 'Upload',
                'TRANSFER_RECEIVE': 'Download',
                'QR_TRANSFER_OUT': 'Upload',
                'QR_TRANSFER_IN': 'Download',
                'COMMISSION_SUCCESS': 'Coins',
                'CRYPTO_SUCCESS': 'Bitcoin',
                'CRYPTO_DEDUCTION': 'Bitcoin',
                'MARKET_MAKER_TRADE': 'TrendingUp',
                'ORDER_FILLED': 'Check',
                'ORDER_CREATED': 'FileText',
                'ACCOUNT_STATEMENT_ENTRY': 'FileText'
            };
            return icons[action] || 'FileText';
        },

        getRowClass(action: string): string {
            if (action.includes('CHARGE') || action.includes('RECEIVED') || action.includes('SUCCESS') || action.includes('DEPOSIT') || action.includes('FILLED') || action.includes('MARKET_MAKER_TRADE') || action.includes('ACCOUNT_STATEMENT_ENTRY') || (action === 'ORDER_FILLED' && this.logs.find(log => log.action === action)?.metadata?.orderType === 'SELL')) {
                return 'row-credit';
            } else if (action.includes('DECHARGE') || action.includes('DEDUCT') || action.includes('BALANCE_DEDUCTED') || action.includes('SENT') || action.includes('WITHDRAWAL') || action.includes('PAYMENT') || action.includes('COMMISSION') || action.includes('FEE') || action.includes('FROZEN') || (action === 'ORDER_FILLED' && this.logs.find(log => log.action === action)?.metadata?.orderType === 'BUY')) {
                return 'row-debit';
            }
            return '';
        },

        getActionBadgeClass(action: string): string {
            if (action.includes('CHARGE') || action.includes('RECEIVED') || action.includes('SUCCESS') || action.includes('DEPOSIT') || action.includes('FILLED') || action.includes('MARKET_MAKER_TRADE') || action.includes('ACCOUNT_STATEMENT_ENTRY')) {
                return 'badge-credit';
            } else if (action.includes('DECHARGE') || action.includes('DEDUCT') || action.includes('BALANCE_DEDUCTED') || action.includes('SENT') || action.includes('WITHDRAWAL') || action.includes('PAYMENT') || action.includes('COMMISSION') || action.includes('FEE') || action.includes('FROZEN')) {
                return 'badge-debit';
            }
            return 'badge-neutral';
        },

        getAmountClass(action: string): string {
            if (action.includes('CHARGE') || action.includes('RECEIVED') || action.includes('SUCCESS') || action.includes('DEPOSIT') || action.includes('FILLED') || action.includes('MARKET_MAKER_TRADE') || action.includes('ACCOUNT_STATEMENT_ENTRY')) {
                return 'amount-credit';
            } else if (action.includes('DECHARGE') || action.includes('DEDUCT') || action.includes('BALANCE_DEDUCTED') || action.includes('SENT') || action.includes('WITHDRAWAL') || action.includes('PAYMENT') || action.includes('COMMISSION') || action.includes('FEE') || action.includes('FROZEN')) {
                return 'amount-debit';
            }
            return '';
        },

        getAmountSign(action: string): string {
            if (action.includes('CHARGE') || action.includes('RECEIVED') || action.includes('SUCCESS') || action.includes('DEPOSIT') || action.includes('FILLED') || action.includes('MARKET_MAKER_TRADE') || action.includes('ACCOUNT_STATEMENT_ENTRY')) {
                return '+';
            } else if (action.includes('DECHARGE') || action.includes('DEDUCT') || action.includes('BALANCE_DEDUCTED') || action.includes('SENT') || action.includes('WITHDRAWAL') || action.includes('PAYMENT') || action.includes('COMMISSION') || action.includes('FEE') || action.includes('FROZEN')) {
                return '-';
            }
            return '';
        },

        getBalanceChange(log: StatementLog): string {
            const prev = parseFloat(log.previousBalance || '0') || 0;
            const curr = parseFloat(log.newBalance || '0') || 0;
            const diff = curr - prev;
            const sign = diff >= 0 ? '+' : '';
            return `${sign}${this.formatCurrency(Math.abs(diff))}`;
        },

        getBalanceChangeClass(log: StatementLog): string {
            const prev = parseFloat(log.previousBalance || '0') || 0;
            const curr = parseFloat(log.newBalance || '0') || 0;
            return curr >= prev ? 'change-positive' : 'change-negative';
        },

        getStatusLabel(status: string): string {
            const labels: Record<string, string> = {
                'SUCCESS': 'موفق',
                'PENDING': 'در انتظار',
                'FAILED': 'ناموفق',
                'CANCELLED': 'لغو شده',
                'PROCESSING': 'در حال پردازش',
                'COMPLETED': 'تکمیل شده',
                'REFUNDED': 'بازگشت شده',
                'FROZEN': 'فریز شده'
            };
            return labels[status] || status;
        },

        goToPage(page: number): void {
            if (page < 1 || page > this.pagination.totalPages) return;
            this.$emit('page-change', page);
        },

        getDisplayBalance(log: StatementLog): string {
            // For ORDER_FILLED logs, use newBalanceRial if balance is 0
            if (log.type === 'FINANCIAL' && log.action === 'ORDER_FILLED' && log.metadata) {
                const balance = parseFloat(log.newBalance || '0');
                if (balance === 0 && log.metadata.newBalanceRial) {
                    return log.metadata.newBalanceRial as string;
                }
            }
            return log.newBalance || '0';
        },

        getAmountTooltip(log: StatementLog): string {
            if (log.type === 'FINANCIAL' && log.action === 'ORDER_FILLED' && log.metadata) {
                return `جزئیات سفارش ${log.metadata.orderType as string} - مقدار: ${log.metadata.totalAmount as string}`;
            }
            return '';
        },

        getOrderTypeLabel(orderType: string): string {
            const labels: Record<string, string> = {
                'BUY': 'خرید',
                'SELL': 'فروش'
            };
            return labels[orderType] || orderType;
        },

        getMetadataBalanceChangeClass(balanceChange: string): string {
            const change = parseFloat(balanceChange);
            return change >= 0 ? 'change-positive' : 'change-negative';
        },

        openMetadataDialog(log: StatementLog): void {
            this.selectedLog = log;
            this.showMetadataDialog = true;
        },

        closeMetadataDialog(): void {
            this.showMetadataDialog = false;
            this.selectedLog = null;
        },

        formatMetadata(metadata: Record<string, unknown>): string {
            return JSON.stringify(metadata, null, 2);
        }
    }
});
